import { useContext, useState } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { motion } from 'motion/react'

import Sidebar from '../components/layout/Sidebar'
import Header from '../components/layout/Header'
import Footer from '../components/layout/Footer'

import { spacing } from '@material-ui/system'
import {
  CssBaseline,
  Hidden,
  Paper as MuiPaper,
  withWidth,
} from '@material-ui/core'
import { BreakpointContext } from '@/context'
import { isNativeMobile } from '../hooks/useIsMobile'
import { useHelpDrawer } from '@/context/HelpDrawerContext'

const drawerWidth = 260

const GlobalStyle = createGlobalStyle`
  html,
  body,
  #root {
    height: 100%;
  }

  body {
    background: ${(props) => props.theme.body.background};
  }

  .MuiCardHeader-action .MuiIconButton-root {
    padding: 4px;
    width: 28px;
    height: 28px;
  }
`

const Root = styled(motion.div)`
  display: flex;
  min-height: 100vh;
`

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`

const AppContent = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    max-width: calc(100% - ${drawerWidth}px);
  }
  ${(props) => props.theme.breakpoints.down('sm')} {
    max-width: 100%;
  }
  flex: 1;
  display: flex;
  flex-direction: column;
`

const Paper = styled(MuiPaper)(spacing)

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.body.background};

  /* Buffer so the floating Zendesk launcher can't cover page content. Only
     needed in the mobile layout (app webview / small widths) — on desktop the
     centered content column and footer keep it clear. */
  ${(props) => props.theme.breakpoints.down('sm')} {
    padding-bottom: 80px;
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`

function Dashboard(props) {
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const { children, routes, width } = props

  const breakpoint = useContext(BreakpointContext)
  const { isOpen: helpDrawerOpen } = useHelpDrawer()

  const helpDrawerWidth = 400
  const shouldShiftContent = helpDrawerOpen && breakpoint.mdAndUp

  return (
    <Root
      initial={false}
      animate={{
        marginRight: shouldShiftContent ? helpDrawerWidth : 0,
      }}
      transition={{
        type: 'spring',
        damping: 30,
        stiffness: 300,
      }}
    >
      <CssBaseline />
      <GlobalStyle />
      {!isNativeMobile() && (
        <Drawer>
          <Hidden mdUp implementation="js">
            <Sidebar
              routes={routes}
              PaperProps={{ style: { width: drawerWidth } }}
              variant="temporary"
              open={mobileOpen}
              onClose={handleDrawerToggle}
            />
          </Hidden>
          <Hidden smDown implementation="js">
            <Sidebar
              routes={routes}
              PaperProps={{ style: { width: drawerWidth } }}
            />
          </Hidden>
        </Drawer>
      )}
      <AppContent>
        <Header onDrawerToggle={handleDrawerToggle} />
        <MainContent>{children}</MainContent>
        <Footer />
      </AppContent>
    </Root>
  )
}

export default withWidth()(Dashboard)
